import { useState, useEffect, useContext } from "react";
import { SocketContext } from "../../context/socket";
import CustomLeaderboardPreview from "../../routes/viewers/CustomLeaderboardPreview";

export default function TeamAggregatePreview({ roundId, teamStandingsData, currentPage = 0, onPreviewDataUpdate }) {
   const socket = useContext(SocketContext);
   const [activeData, setActiveData] = useState(null);
   const [show, setShow] = useState(true);

   useEffect(() => {
      if (teamStandingsData) {
         try {
            const parsed = typeof teamStandingsData === 'string' ? JSON.parse(teamStandingsData) : teamStandingsData;
            const standings = parsed?.teamStandings || parsed || [];
            
            // Transform team standings into the format expected by CustomLeaderboardPreview
            const rows = standings.map((team, index) => ({
               Rank: team.Rank || index + 1,
               Team: team.Team || "",
               Win: team.Win || 0,
               Loss: team.Loss || 0,
               Draw: team.Draw || 0,
               WinPercent: team.WinPercent || 0,
               PlayerName: null, // Team data, not player data
            }));

            // Determine title and round based on roundId
            if (roundId === 9999999 || roundId === '9999999') {
               // Round 0 (Alphabetical) - use passed data if available, otherwise get all teams
               if (standings && standings.length > 0) {
                  // Use the passed alphabetical standings data
                  setActiveData({
                     Title: "TEAM STANDINGS",
                     // Don't set Round or StandingsId - no subtitle
                     rows: rows,
                     Columns: 1,
                     Rows: 8,
                     Archetype: 0, // No archetype/team column for team aggregate
                  });
               } else {
                  // Get all teams, sort alphabetically, add rank, set W L D % to 0
                  socket.emit("getPlayers", (allPlayers) => {
                     if (allPlayers && allPlayers.length > 0) {
                        // Get unique teams
                        const teamSet = new Set();
                        allPlayers.forEach(player => {
                           const team = player.TEAM;
                           if (team && team.trim() !== "") {
                              teamSet.add(team);
                           }
                        });
                        
                        // Convert to array and sort alphabetically
                        const sortedTeams = Array.from(teamSet).sort((a, b) => {
                           return a.localeCompare(b);
                        });
                        
                        // Create rows with rank and 0 stats
                        const alphabeticalRows = sortedTeams.map((team, index) => ({
                           Rank: index + 1,
                           Team: team,
                           Win: 0,
                           Loss: 0,
                           Draw: 0,
                           WinPercent: 0,
                           PlayerName: null, // Team data, not player data
                        }));
                        
                        setActiveData({
                           Title: "TEAM STANDINGS",
                           // Don't set Round or StandingsId - no subtitle
                           rows: alphabeticalRows,
                           Columns: 1,
                           Rows: 8,
                           Archetype: 0, // No archetype/team column for team aggregate
                        });
                     }
                  });
               }
            } else if (roundId === 0 || roundId === '0') {
               // Most Recent - show "TEAM STANDINGS" title, round subtitle from Round in JSON
               const roundName = parsed?.Round || "Most Recent";
               
               setActiveData({
                  Title: "TEAM STANDINGS",
                  Round: roundName,
                  StandingsId: roundId, // Set to show Round
                  rows: rows,
                  Columns: 1,
                  Rows: 8,
                  Archetype: 0, // No archetype/team column for team aggregate
                  showRoundInOrange: true, // Flag to show round in orange
               });
            } else {
               // Specific round selected - show "TEAM STANDINGS" title, round subtitle from tournament rounds
               socket.emit("getTournamentRounds", (rounds) => {
                  const round = rounds?.find(r => r.ID === roundId) || { Name: "Round " + roundId };
                  
                  setActiveData({
                     Title: "TEAM STANDINGS",
                     Round: round.Name,
                     StandingsId: roundId, // Set to show Round
                     rows: rows,
                     Columns: 1,
                     Rows: 8,
                     Archetype: 0, // No archetype/team column for team aggregate
                     showRoundInOrange: true, // Flag to show round in orange
                  });
               });
            }
         } catch (error) {
            console.error("Error parsing team standings data:", error);
         }
      }
   }, [roundId, teamStandingsData]);

   // Expose activeData to parent via callback
   useEffect(() => {
      if (onPreviewDataUpdate && activeData) {
         onPreviewDataUpdate(activeData);
      }
   }, [activeData, onPreviewDataUpdate]);

   if (!activeData) {
      return null;
   }

   return (
      <CustomLeaderboardPreview
         currentPage={currentPage}
         activeData={activeData}
         show={show}
         preview={true}
      />
   );
}
